import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { Profile } from '../types/database.tables'

export const useProfile = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadProfile = useCallback(async () => {
    if (!user) {
      setProfile(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const { data, error: dbError } = await supabase.from('profiles').select('*').eq('id', user.id).single()

      if (dbError) {
        throw dbError
      }

      setProfile(data)
    } catch (err) {
      console.error('Failed to load user profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to load profile')
      setProfile(null)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Load profile when user changes
  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  return {
    profile,
    isLoading,
    error,
    loadProfile,
    refreshProfile: loadProfile, // Alias for consistency
  }
}
